import { Component,  EventEmitter,  Input, Output } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Asset } from 'globals/store/assets.data';
import { Cue } from 'globals/store/events.data';
import { CueProgram, CueSession } from 'globals/store/programs.data';
import { moveIndexItemDownInList, moveIndexItemUpInList, removeFromListByIndex } from '../../helpers/helpers';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { DeleteButtonComponent } from 'src/app/components/buttons/delete-button.component';
import { UpDownArrowsComponent } from 'src/app/components/misc/up-down-arrows.component';
import { CommonModule } from '@angular/common';
import { CueJustTime, JustTimeToHHMMSS } from 'src/app/services/pipes/justTimeToHHMM.pipe';
import { JoiHMInputComponent } from 'src/app/components/joi-input/joi-hm-input.component';
import { FilterOutHiddenArrayPipe } from 'src/app/services/pipes/filterOutHidden.pipe';
import { AssetLinkComponent } from '../assets/asset-link.component';
import { IdsToItemsPipe } from 'src/app/services/pipes/sortByIds.pipe';
import { MatButtonModule } from '@angular/material/button';


export interface AddCueData { key: string, index: number, type: 'cue' | 'asset', event: MouseEvent }
interface RemoveCueData { key: string, index: number, i: number }


@Component({
    selector: 'cue-sheet-view',
    template: `
<ng-template #cue let-key="key" let-cueIds="cueIds" let-assetId="assetId" let-index="index">
  <ng-container *ngIf="{ asset: assets ? assets[assetId] : null } as vars">
    <div class="cell cue-options">
      <div class="half-holder" *ngFor="let cue of cueIds | idsToItems:cues; index as i">
        <span class="cue">{{ cue.name }}</span>
        <div class="remove" *ngIf="!readOnly" (click)="removeCue({ key: key, index: index, i: i })">x</div>
      </div>
      <button mat-button style="cursor: pointer" class="plain" *ngIf="!readOnly" (click)="addCue.emit({ key: key, index: index, type: 'cue', event: $event })">ADD CUE</button>
      <button mat-button style="cursor: pointer" class="plain" *ngIf="!readOnly && !vars.asset" (click)="addCue.emit({ key: key, index: index, type: 'asset', event: $event })">ADD ASSET</button>
      <div class="half-holder" *ngIf="!!vars.asset">
        <asset-link *ngIf="!!vars.asset"
          (click)="$event.stopPropagation()"
          [asset]="vars.asset"
          [onlyLink]="true"
        ></asset-link>
        <div class="remove" *ngIf="!readOnly" (click)="removeAsset(key, index)">x</div>
      </div>
    </div>
  </ng-container>
</ng-template>

<div class="pre-header">
  <div class="title-holder">
    <h3>{{ number }}.&nbsp;&nbsp;</h3>
    <span class="times">{{ session.start }} - {{ session.end }} [{{ session.duration }}]</span>
    <h5>{{ session.title || "Untitled session" }}</h5>
  </div>
  <div class="copy-holder">
    <button *ngIf="!readOnly" mat-button class="plain" (click)="copyInto.emit($event)">COPY INTO OTHER SESSION</button>
  </div>
</div>

<div class="cue-sheet" *ngIf="program.cueCols && { cols: program.cueCols | filterOutHiddenArray:program.hiddenCueCols || [] } as cols" 
  [ngClass]="{ readOnly: readOnly, out: session.type !== 'session' }" [style.--cuecols]="cols.cols.length">
  <div class="header" *ngIf="!readOnly"></div>
  <div class="header cue" (click)="readOnly ? null : rearrangeCues.emit()">Cue</div>
  <div class="header">Time</div>
  <div class="header">Action</div>
  <div class="header">Duration</div>
  <div class="header">Cue Point</div>
  <div class="header" *ngFor="let cueCol of cols.cols">
    {{ cueCol.name }}
  </div>
  <ng-container *ngFor="let row of session.cues?.rows; index as index; first as first; last as last; trackBy: trackByIndex">
    
    <div class="cell actions" *ngIf="!readOnly">
      <up-down-arrows *ngIf="!readOnly" [hideUp]="first" [hideDown]="last" (moveUp)="moveUp(index)" (moveDown)="moveDown(index)"></up-down-arrows>
      <delete-button [deleteTitle]="deleteTitle" [deleteText]="deleteText" (delete)="deleteLine(index)"></delete-button>
    </div>
    <div class="cell cue" [ngClass]="{ app: !readOnly }" (click)="readOnly ? null : rearrangeCues.emit()">
      {{ index + 1 }}
    </div>
    @if(session && session.cues){    
      <div class="cell" [ngClass]="{ grayed: !readOnly }">{{ session.cues | cueJustTime:index }}</div>
    }
    <div class="cell">
      <joi-input
        [(model)]="row.action" 
        (joiChange)="onSave.emit()" 
        [disabled]="readOnly" 
        [placeholder]="readOnly ? '' : 'Cue action...'"
        type="textarea"
      ></joi-input>
    </div>
    <div class="cell duration">
      <joi-hm-input *ngIf="!readOnly"
        [allowNoValue]="true" 
        [showSeconds]="true"
        [minMinutes]="0"
        [maxMinutes]="59"
        [maxHours]="9"
        [readOnly]="readOnly"
        (timeChange)="row.duration = $event.justTime; onSave.emit()" 
        [startingJustTime]="row.duration || { h: 0, m: 0, s: 0 }" [minMinutes]="0"
      ></joi-hm-input>
      <span *ngIf="!!readOnly">{{ (row.duration || { h: 0, m: 0, s: 0 }) | justTimeToHHMMSS }}</span>
    </div>
    <div class="cell">
      <joi-input
        [(model)]="row.point" 
        (joiChange)="onSave.emit()" 
        [disabled]="readOnly" 
        [placeholder]="readOnly ? '' : 'Cue point...'"
        type="textarea"
      ></joi-input>
    </div>
    <ng-container *ngFor="let cueCol of cols.cols">
      <ng-container *ngIf="{ cue: row.cues[cueCol.id] } as c" [ngTemplateOutlet]="cue" 
        [ngTemplateOutletContext]="{ index: index, cueIds: c.cue?.cueIds, assetId: c.cue?.assetId, key: cueCol.id }"
      ></ng-container>
    </ng-container>
  </ng-container>
</div>
<button *ngIf="!readOnly" matButton class="plain" (click)="addLine()">+ ADD LINE</button>


`,
    styleUrls: ['./cue-sheet-view.scss'],
    standalone: true,
    imports: [JoiInputComponent, MatButtonModule, JustTimeToHHMMSS, FilterOutHiddenArrayPipe, AssetLinkComponent, IdsToItemsPipe, JoiHMInputComponent, DeleteButtonComponent, UpDownArrowsComponent, CommonModule, CueJustTime],
})
export class CueSheetViewComponent<S extends CueSession, P extends CueProgram> {
  @Input() readOnly: boolean
  @Input() assets: Dictionary<Asset>
  @Input() cues: Dictionary<Cue>
  @Input() session: S
  @Input() program: P
  @Input() number: number

  @Output() onSave: EventEmitter<void> = new EventEmitter()
  @Output() addCue: EventEmitter<AddCueData> = new EventEmitter() 
  @Output() copyInto: EventEmitter<MouseEvent> = new EventEmitter() 
  @Output() rearrangeCues: EventEmitter<void> = new EventEmitter()

  deleteTitle = 'Delete Cue Line'
  deleteText = 'Are you sure you want to delete this cue line?'

  addLine() {
    this.session.cues.rows.push({ cues: {} })
    this.onSave.emit()
  }

  deleteLine(index: number) {
    removeFromListByIndex(this.session.cues.rows, index)
    this.onSave.emit()
  }

  removeCue({ key, index,  i }: RemoveCueData) {
    removeFromListByIndex(this.session.cues.rows[index].cues[key].cueIds, i)
    this.onSave.emit()
  }

  removeAsset(key: string, index: number) {
    delete this.session.cues.rows[index].cues[key].assetId
    this.onSave.emit()
  }

  moveUp(i: number) {
    this.session.cues.rows = moveIndexItemUpInList(i, this.session.cues.rows)
    this.onSave.emit()
  }

  moveDown(i: number) {
    this.session.cues.rows = moveIndexItemDownInList(i, this.session.cues.rows)
    this.onSave.emit()
  }

  trackByIndex(index) {
    return index
  }

  
}